"use client";

import { useState } from "react";
import {
  Checkbox,
  CopyButton,
  PillGroup,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_PRIMARY,
  BUTTON_SECONDARY,
  HINT,
} from "@/components/tool-kit/styles";
import { useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";

type Variant = "kwl" | "kwhl" | "kwl-plus";
type Column = "k" | "w" | "h" | "l";
type Section = Column | "map" | "summary";
type Orientation = "portrait" | "landscape";

const COLUMNS: Record<Variant, Column[]> = {
  kwl: ["k", "w", "l"],
  kwhl: ["k", "w", "h", "l"],
  "kwl-plus": ["k", "w", "l"],
};

const GRID_CLASS: Record<Variant, string> = {
  kwl: "sm:grid-cols-3 print:grid-cols-3",
  kwhl: "sm:grid-cols-2 lg:grid-cols-4 print:grid-cols-4",
  "kwl-plus": "sm:grid-cols-3 print:grid-cols-3",
};

const LINE_CHOICES = ["6", "10", "14", "18", "22"] as const;
const LINE_BREAK = /\r?\n/;

/** English strings. A translated content file overrides these through `ui`. */
export const KWL_CHART_GENERATOR_STRINGS = {
  topicLabel: "Topic",
  topicPlaceholder: "Volcanoes",
  variantLabel: "Chart type",
  variants: { kwl: "KWL", kwhl: "KWHL", "kwl-plus": "KWL+" },
  variantHints: {
    kwl: "Know, Want to know, Learned — Donna Ogle's original three columns.",
    kwhl: "Adds an H column: how will I find out? Useful for research projects.",
    "kwl-plus":
      "Adds a concept map and a written summary after the L column (Carr & Ogle, 1987).",
  },
  headings: {
    k: "K — What I Know",
    w: "W — What I Want to Know",
    h: "H — How I Will Find Out",
    l: "L — What I Learned",
    map: "Concept map",
    summary: "Summary",
  },
  prompts: {
    k: "What do I already know, or think I know, about this topic?",
    w: "What questions do I want to answer?",
    h: "Where and how could I find the answers?",
    l: "What did I learn? Which of my questions did it answer?",
    map: "Sort what you learned into categories and connect them to the topic.",
    summary: "Use the map to write a summary in your own words.",
  },
  entryPlaceholder: "One idea per line",
  options: "Options",
  showNameLine: "Name and date line",
  orientationLabel: "Page orientation",
  portrait: "Portrait",
  landscape: "Landscape",
  linesLabel: "Blank lines per column when printed",
  print: "Print chart",
  copyText: "Copy chart as text",
  copied: "Copied!",
  clear: "Clear entries",
  chartTitle: "{variant} Chart: {topic}",
  chartTitleNoTopic: "{variant} Chart",
  fillHint:
    "Type in the boxes to fill the chart on screen, or leave them empty to print a blank chart. Entries print above the blank lines.",
  name: "Name",
  date: "Date",
} as const;

type Strings = typeof KWL_CHART_GENERATOR_STRINGS;
type Entries = Record<Section, string>;

const EMPTY: Entries = { k: "", w: "", h: "", l: "", map: "", summary: "" };

function lines(text: string): string[] {
  return text
    .split(LINE_BREAK)
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
}

function sectionsFor(variant: Variant): Section[] {
  return variant === "kwl-plus"
    ? [...COLUMNS[variant], "map", "summary"]
    : COLUMNS[variant];
}

function chartText(
  title: string,
  variant: Variant,
  entries: Entries,
  strings: Strings
): string {
  return [
    title,
    "",
    ...sectionsFor(variant).flatMap((section) => [
      strings.headings[section],
      ...lines(entries[section]).map((line) => `- ${line}`),
      "",
    ]),
  ]
    .join("\n")
    .trim();
}

function Ruled({ count }: { count: number }) {
  return (
    <div aria-hidden="true" className="hidden print:block">
      {Array.from({ length: count }, (_, index) => (
        <div
          className="h-8 border-gray-400 border-b"
          key={`line-${index + 1}`}
        />
      ))}
    </div>
  );
}

function SectionBox({
  section,
  value,
  onChange,
  strings,
  ruledLines,
  tall,
}: {
  section: Section;
  value: string;
  onChange: (value: string) => void;
  strings: Strings;
  ruledLines: number;
  tall?: boolean;
}) {
  const typed = lines(value);
  const id = `kwl-${section}`;
  return (
    <div className="flex flex-col gap-2 rounded-lg border border-border p-3 print:break-inside-avoid print:rounded-none print:border-black">
      <label className="font-semibold text-sm" htmlFor={id}>
        {strings.headings[section]}
      </label>
      <p className="text-xs italic opacity-80">{strings.prompts[section]}</p>
      <textarea
        className="min-h-[160px] w-full resize-y rounded-md border border-border bg-card p-2 text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary print:hidden"
        id={id}
        onChange={(event) => onChange(event.target.value)}
        placeholder={strings.entryPlaceholder}
        value={value}
      />
      {typed.length > 0 ? (
        <ul className="hidden list-disc space-y-1 ps-5 text-sm print:block">
          {typed.map((line, index) => (
            <li key={`${section}-${index + 1}-${line}`}>{line}</li>
          ))}
        </ul>
      ) : null}
      {tall ? (
        <div aria-hidden="true" className="hidden h-[3.5in] print:block" />
      ) : (
        <Ruled count={Math.max(2, ruledLines - typed.length)} />
      )}
    </div>
  );
}

export default function KwlChartGeneratorWidget() {
  const strings = useToolUi(KWL_CHART_GENERATOR_STRINGS);
  const [topic, setTopic] = useState("");
  const [variant, setVariant] = useState<Variant>("kwl");
  const [nameLine, setNameLine] = useState(true);
  const [orientation, setOrientation] = useState<Orientation>("portrait");
  const [lineCount, setLineCount] =
    useState<(typeof LINE_CHOICES)[number]>("14");
  const [entries, setEntries] = useState<Entries>(EMPTY);

  const label = strings.variants[variant];
  const title = topic.trim()
    ? t(strings.chartTitle, { variant: label, topic: topic.trim() })
    : t(strings.chartTitleNoTopic, { variant: label });
  const ruledLines = Number(lineCount);
  const set = (section: Section) => (value: string) =>
    setEntries((previous) => ({ ...previous, [section]: value }));

  return (
    <div className="space-y-6">
      {orientation === "landscape" ? (
        <style>{"@page { size: landscape; }"}</style>
      ) : null}
      <div className="space-y-4 print:hidden">
        <TextField
          id="kwl-topic"
          label={strings.topicLabel}
          onChange={setTopic}
          placeholder={strings.topicPlaceholder}
          value={topic}
        />
        <PillGroup
          label={strings.variantLabel}
          name="kwl-variant"
          onChange={setVariant}
          options={(["kwl", "kwhl", "kwl-plus"] as const).map((value) => ({
            value,
            label: strings.variants[value],
          }))}
          value={variant}
        />
        <p className={HINT}>{strings.variantHints[variant]}</p>
        <div className="grid gap-4 sm:grid-cols-2">
          <SelectField
            id="kwl-orientation"
            label={strings.orientationLabel}
            onChange={setOrientation}
            options={[
              { value: "portrait", label: strings.portrait },
              { value: "landscape", label: strings.landscape },
            ]}
            value={orientation}
          />
          <SelectField
            id="kwl-lines"
            label={strings.linesLabel}
            onChange={setLineCount}
            options={LINE_CHOICES.map((value) => ({ value, label: value }))}
            value={lineCount}
          />
        </div>
        <fieldset className="space-y-2">
          <legend className="font-medium text-foreground text-sm">
            {strings.options}
          </legend>
          <Checkbox
            checked={nameLine}
            label={strings.showNameLine}
            onChange={setNameLine}
          />
        </fieldset>
        <div className="flex flex-wrap gap-3">
          <button
            className={BUTTON_PRIMARY}
            onClick={() => window.print()}
            type="button"
          >
            {strings.print}
          </button>
          <CopyButton
            copiedLabel={strings.copied}
            label={strings.copyText}
            text={chartText(title, variant, entries, strings)}
          />
          <button
            className={BUTTON_SECONDARY}
            onClick={() => setEntries(EMPTY)}
            type="button"
          >
            {strings.clear}
          </button>
        </div>
        <p className={HINT}>{strings.fillHint}</p>
      </div>

      <section className="space-y-4 text-foreground print:text-black">
        <h2 className="font-semibold text-xl">{title}</h2>
        {nameLine ? (
          <div className="flex flex-wrap gap-x-10 gap-y-3 text-sm">
            <span className="flex items-end gap-2">
              {strings.name}
              <span className="inline-block w-56 border-current border-b" />
            </span>
            <span className="flex items-end gap-2">
              {strings.date}
              <span className="inline-block w-32 border-current border-b" />
            </span>
          </div>
        ) : null}
        <div className={`grid grid-cols-1 gap-3 ${GRID_CLASS[variant]}`}>
          {COLUMNS[variant].map((column) => (
            <SectionBox
              key={column}
              onChange={set(column)}
              ruledLines={ruledLines}
              section={column}
              strings={strings}
              value={entries[column]}
            />
          ))}
        </div>
        {variant === "kwl-plus" ? (
          <div className="grid grid-cols-1 gap-3">
            <SectionBox
              onChange={set("map")}
              ruledLines={ruledLines}
              section="map"
              strings={strings}
              tall
              value={entries.map}
            />
            <SectionBox
              onChange={set("summary")}
              ruledLines={Math.max(4, Math.round(ruledLines / 2))}
              section="summary"
              strings={strings}
              value={entries.summary}
            />
          </div>
        ) : null}
      </section>
    </div>
  );
}
